import { ComponentType } from "react";

import classNames from "../../utils/classNames";

export interface DailyCheckIcon {
  // The key stored on the definition and sent to the API, unchanged.
  id: string;
  name: string;
  Icon: ComponentType<{ className?: string }>;
}

// Renders a Material Symbols glyph by ligature name. The font is loaded in index.html
// and subset to the keys below, so adding a key here means adding it there too.
const materialSymbol = (name: string) => {
  const MaterialSymbol = ({ className }: { className?: string }) => (
    <span className={classNames("material-symbols-outlined", className)} aria-hidden="true">
      {name}
    </span>
  );

  return MaterialSymbol;
};

const icon = (id: string, name: string): DailyCheckIcon => ({
  id: id,
  name: name,
  Icon: materialSymbol(id),
});

/*
 * These are the only keys the Wilma Yard app can render. It maps them to Material
 * Symbols in lib/features/daily_checks/daily_checks_definitions.dart and falls back to
 * `checklist` for anything it doesn't recognise, so this list must stay in step with
 * that switch — and with the subset font loaded in index.html.
 *
 * Grouped in the same order as the app's switch so related icons sit together.
 */
export const dailyCheckIcons: DailyCheckIcon[] = [
  // Site & buildings
  icon("domain", "Site"),
  icon("warehouse", "Warehouse"),
  icon("factory", "Factory"),
  icon("garage", "Garage"),

  // Plant & vehicles
  icon("forklift", "Forklift"),
  icon("front_loader", "Loading shovel"),
  icon("agriculture", "360 / plant"),
  icon("construction", "Construction"),
  icon("precision_manufacturing", "Machinery"),
  icon("conveyor_belt", "Conveyor"),
  icon("local_shipping", "Lorry"),
  icon("fire_truck", "Fire engine"),
  icon("directions_car", "Car"),
  icon("two_wheeler", "Motorbike"),
  icon("car_repair", "Vehicle repair"),
  icon("tire_repair", "Tyres"),

  // Health & safety
  icon("health_and_safety", "PPE / safety"),
  icon("fire_extinguisher", "Fire extinguisher"),
  icon("emergency", "Emergency"),
  icon("warning", "Hazard"),
  icon("medical_services", "First aid"),
  icon("engineering", "Hard hat"),
  icon("security", "Security"),
  icon("visibility", "Visual inspection"),

  // Fuel, fluids & power
  icon("local_gas_station", "Fuel tank"),
  icon("propane_tank", "Gas bottle"),
  icon("oil_barrel", "Oil / fluids"),
  icon("electric_bolt", "Electrical"),
  icon("ev_station", "EV charging"),
  icon("water_drop", "Water"),
  icon("water_damage", "Spill / leak"),
  icon("lightbulb", "Lighting"),

  // Equipment & maintenance
  icon("elevator", "Lift"),
  icon("compress", "Baler"),
  icon("build", "Tools"),
  icon("handyman", "Maintenance"),
  icon("plumbing", "Plumbing"),
  icon("cleaning_services", "Cleaning"),
  icon("battery_charging_full", "Battery"),
  icon("monitor_weight", "Weighbridge"),

  // Stock & waste
  icon("inventory_2", "Stock"),
  icon("pallet", "Pallet"),
  icon("recycling", "Recycling"),
  icon("delete", "Waste"),

  // Security & access
  icon("lock", "Lock up"),
  icon("key", "Keys"),
  icon("videocam", "CCTV"),
];

// What the app shows when a definition has no icon, or one it doesn't recognise.
export const dailyCheckFallbackIcon: DailyCheckIcon = icon("checklist", "Checklist");

export const findDailyCheckIcon = (iconKey?: string | null) =>
  iconKey ? dailyCheckIcons.find((x) => x.id === iconKey) : undefined;
